import { isOmittedSourceType } from '#pplx/frontend/apps/ask/sources/hooks/internal/isOmittedSource.js';
import { isSourceIdConnectedByMigration } from '#pplx/frontend/apps/ask/sources/hooks/sourceStatusPredicates.js';
import { getFallbackSourceLabel } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLabel.js';
import { isAvailableSourceType } from '#pplx/frontend/apps/ask/sources/types/availableSources.js';
import { getCometMcpSourceType, isCometMcpSourceType, } from '#pplx/frontend/apps/ask/sources/types/cometMcpSourceTypes.js';
import { sortSourcesAlphabetically } from '#pplx/frontend/apps/ask/sources/utilities/sortSourcesAlphabetically.js';
import { sortSourcesCoreSourcesToTop } from '#pplx/frontend/apps/ask/sources/utilities/sortSourcesCoreSourcesToTop.js';
import { sortSourcesWebToTop } from '#pplx/frontend/apps/ask/sources/utilities/sortSourcesWebToTop.js';
export function createSourceAllowedPredicate({ isCometMcpSourceAllowed, isOmitted, omitCometMcpSources, isApiSourceAllowed, }) {
    return (sourceType) => {
        if (isOmittedSourceType(sourceType)) {
            return false;
        }
        // Check comet/local MCP before isOmitted — these sources have no API entry so
        // getApiSource returns undefined, which would cause isOmitted to return true
        // even when no sources are being filtered out.
        if (isCometMcpSourceType(sourceType)) {
            return omitCometMcpSources ? false : isCometMcpSourceAllowed(sourceType);
        }
        if (isOmitted(sourceType)) {
            return false;
        }
        return isApiSourceAllowed(sourceType);
    };
}
export function createSourceConnectedPredicate({ isCometMcpSourceAllowed, isOmitted, omitCometMcpSources, getIsApiSourceConnected, apiSources, }) {
    return (sourceType) => {
        if (isOmittedSourceType(sourceType)) {
            return false;
        }
        if (isCometMcpSourceType(sourceType)) {
            return omitCometMcpSources ? false : isCometMcpSourceAllowed(sourceType);
        }
        if (isOmitted(sourceType)) {
            // Hidden legacy source ids (e.g. `google_slides__pipedream`) have no
            // direct API entry, so `isOmitted` reports them as omitted. They can
            // still be connected when a migration projects the legacy id onto a
            // connected target source, so consult the migration predicate before
            // treating them as disconnected.
            return isSourceIdConnectedByMigration(sourceType, apiSources);
        }
        return getIsApiSourceConnected(sourceType);
    };
}
export function createSourceLabelGetter({ getApiSource, getAvailableSourceLabel, }) {
    return (sourceType) => {
        const apiSource = getApiSource(sourceType);
        if (apiSource) {
            return apiSource.display_name;
        }
        if (isAvailableSourceType(sourceType)) {
            return getAvailableSourceLabel(sourceType);
        }
        return getFallbackSourceLabel(sourceType);
    };
}
const SOURCE_SORTS = [sortSourcesAlphabetically, sortSourcesCoreSourcesToTop, sortSourcesWebToTop];
export function getSourceIds({ apiSources, isSourceAllowed, cometMcpSources, getSourceLabel, }) {
    // Create a list of all the comet MCP source types that the current user has access to.
    const cometSourceTypes = Object.keys(cometMcpSources)
        .map((serverName) => getCometMcpSourceType(serverName))
        .filter(isCometMcpSourceType)
        .filter(isSourceAllowed);
    const perplexitySourceTypes = apiSources
        .filter((source) => isSourceAllowed(source.id))
        .map((source) => source.id);
    return SOURCE_SORTS.reduce((sources, sortFunction) => {
        return sortFunction(sources, { getSourceLabel });
    }, [...perplexitySourceTypes, ...cometSourceTypes]);
}
//# sourceMappingURL=sourceProjection.js.map